import { computed, makeObservable } from "mobx";
import { DateTime } from "luxon";
import type { WithAppStore } from "@/store/types";
import {
  type BillingSubscriptionData,
  type BillingSubscriptionProvider,
  type BillingSubscriptionStatus,
  SubscriptionPlanKind,
  type SubscriptionPlanVariant,
  type SubscriptionTier,
} from "@/store/subscriptions/types";

export class BillingSubscriptionObservable {
  private data: BillingSubscriptionData;
  private store: WithAppStore["store"];

  constructor({ data, store }: { data: BillingSubscriptionData } & WithAppStore) {
    this.data = data;
    this.store = store;

    makeObservable<this, "data" | "store">(this, {
      data: false,
      store: false,
      provider: computed,
      providerSubscriptionId: computed,
      tier: computed,
      storedPlanKind: computed,
      planKind: computed,
      planVariant: computed,
      status: computed,
      currentPeriodStart: computed,
      currentPeriodEnd: computed,
      subscriptionEndsAt: computed,
      effectiveSubscriptionEndsAt: computed,
      isPaid: computed,
      isActivePaid: computed,
      isFree: computed,
      isStoredProactive: computed,
      isProactive: computed,
      isActive: computed,
      isPastDue: computed,
      isCanceled: computed,
      isEnding: computed,
      hasExpiredEnd: computed,
      isScheduledToEnd: computed,
    });
  }

  get provider(): BillingSubscriptionProvider | null {
    if (!this.data.provider) {
      return null;
    }

    return this.data.provider as BillingSubscriptionProvider;
  }

  get providerSubscriptionId(): string | null {
    return this.data.provider_subscription_id ?? null;
  }

  get tier(): SubscriptionTier {
    return this.store.platformInfo.subscriptionWasCompletedSuccessfully === true
      ? "paid"
      : this.data.tier;
  }

  get storedPlanKind(): SubscriptionPlanKind {
    if (this.data.plan_kind) {
      return this.data.plan_kind;
    }

    if ((this.data.tier as string) === SubscriptionPlanKind.PROACTIVE) {
      return SubscriptionPlanKind.PROACTIVE;
    }

    if (this.data.tier === "paid") {
      return SubscriptionPlanKind.PRO;
    }

    if (this.data.tier === "free") {
      return SubscriptionPlanKind.FREE;
    }

    return SubscriptionPlanKind.UNKNOWN;
  }

  get planKind(): SubscriptionPlanKind {
    if (this.store.platformInfo.subscriptionWasCompletedSuccessfully === true) {
      return this.store.platformInfo.completedSubscriptionPlanKind ?? SubscriptionPlanKind.PRO;
    }

    return this.storedPlanKind;
  }

  get planVariant(): SubscriptionPlanVariant | null {
    if (!this.data.plan_variant) {
      return null;
    }

    return this.data.plan_variant as SubscriptionPlanVariant;
  }

  get status(): BillingSubscriptionStatus {
    return this.data.status;
  }

  get currentPeriodStart(): string {
    return this.data.current_period_start;
  }

  get currentPeriodEnd(): string | null {
    return this.data.current_period_end;
  }

  get subscriptionEndsAt(): string | null {
    return this.data.subscription_ends_at ?? null;
  }

  get effectiveSubscriptionEndsAt(): string | null {
    return this.subscriptionEndsAt ?? (this.isCanceled ? this.currentPeriodEnd : null);
  }

  get isPaid(): boolean {
    return (
      this.planKind === SubscriptionPlanKind.PRO ||
      this.planKind === SubscriptionPlanKind.PROACTIVE ||
      this.tier === "paid"
    );
  }

  get isActivePaid(): boolean {
    return this.data.is_paid;
  }

  get isFree(): boolean {
    return this.planKind === SubscriptionPlanKind.FREE;
  }

  get isStoredProactive(): boolean {
    return this.storedPlanKind === SubscriptionPlanKind.PROACTIVE;
  }

  get isProactive(): boolean {
    return this.planKind === SubscriptionPlanKind.PROACTIVE;
  }

  get isActive(): boolean {
    return this.status === "active";
  }

  get isPastDue(): boolean {
    return this.status === "past_due";
  }

  get isCanceled(): boolean {
    return this.status === "canceled";
  }

  get isEnding(): boolean {
    return this.subscriptionEndsAt !== null || this.isCanceled;
  }

  get hasExpiredEnd(): boolean {
    const effectiveEnd = this.effectiveSubscriptionEndsAt;
    if (!effectiveEnd) {
      return false;
    }

    const endDate = DateTime.fromISO(effectiveEnd);
    if (!endDate.isValid) {
      return false;
    }

    return endDate < DateTime.local().startOf("day");
  }

  get isScheduledToEnd(): boolean {
    return this.isEnding && !this.hasExpiredEnd;
  }
}
